"use client"

import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn("relative overflow-hidden", className)}
    {...props}
  >
    {/* Two corrections to the primitive's own layout:

        `max-h-[inherit]` so a `max-h-*` on the Root actually bounds the scroller.
        The Viewport is what scrolls (Radix gives it `overflow-y: scroll` inline),
        but `h-full` against an auto-height Root resolves to `auto` — the Viewport
        grows to fit its content, never scrolls, and the Root's overflow-hidden
        just clips the overflow out of reach. Roots with a fixed `h-*` inherit
        `max-height: none` and are unaffected.

        `[&>div]:block!` overrides the inline `display: table` Radix puts on its
        content wrapper. A table box is shrink-to-fit, so ANY descendant with a
        large intrinsic width — nowrap text, a long unbreakable token, a
        `-webkit-box` from line-clamp — sizes the whole list wider than its
        container and the overflow gets clipped away by the Root. As a block it
        simply fills the Viewport and descendants wrap inside it. The table exists
        to enable horizontal scrolling, which nothing here uses (no ScrollArea in
        this app renders a horizontal ScrollBar). Needs `!` to beat the inline style. */}
    <ScrollAreaPrimitive.Viewport className="h-full max-h-[inherit] w-full rounded-[inherit] [&>div]:block!">
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
))
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
